import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaUser,
  FaEnvelope,
  FaCity,
  FaPhone,
  FaMapMarkerAlt,
  FaIdCard,
  FaUserShield,
} from "react-icons/fa";

const AdvertiserProfile = () => {
  const [profileData, setProfileData] = useState({
    name: "",
    email: "",
    mobileNumber: "",
    reporterId: "",
    role: "",
    residenceaddress: "",
    state: "",
    city: "",
    createdAt: "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(profileData);

  useEffect(() => {
    fetchProfileData();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProfileData(formData);
    setIsEditing(false);
  };

  const fetchProfileData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/get/user/profile`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setProfileData(response.data.data);
    } catch (error) {
      console.error("Error fetching profile data:", error);
    }
  };

  const ProfileField = ({ icon: Icon, label, value, name }) => (
    <div className="group flex flex-col gap-1.5">
      <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest pl-1">
        {label}
      </label>
      <div className="flex items-center gap-3 bg-white p-3 sm:p-4 rounded-xl border-2 border-slate-100 shadow-sm transition-all duration-300 group-hover:border-indigo-200 group-hover:shadow-md">
        <div className="w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110 group-hover:bg-indigo-600 group-hover:text-white">
          <Icon className="text-indigo-600 w-4 h-4 group-hover:text-white transition-colors" />
        </div>
        {isEditing ? (
          <input
            type="text"
            name={name}
            value={formData[name]}
            onChange={handleChange}
            className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 text-sm font-bold text-slate-900 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all"
          />
        ) : (
          <p className="text-sm sm:text-base font-black text-slate-800 break-all leading-tight">{value || "N/A"}</p>
        )}
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto p-2 sm:p-4 md:p-6 mb-8 mt-2 space-y-4">
      <div className="flex flex-col gap-1 mb-4">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-black text-slate-900 tracking-tight uppercase">Profile Details</h1>
        <p className="text-xs sm:text-sm font-bold text-slate-500 tracking-wide">Manage your advertiser account information</p>
      </div>

      <div className="bg-white rounded-[2rem] border border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden">
        {/* Header Profile Section */}
        <div className="relative bg-gradient-to-br from-indigo-700 via-blue-600 to-indigo-900 px-6 py-6 sm:py-8 text-white flex flex-col sm:flex-row items-center sm:items-end justify-start gap-4 sm:gap-6 overflow-hidden">
          {/* Abstract Background Design */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-40 h-40 bg-indigo-400 opacity-20 rounded-full blur-2xl -ml-10 -mb-10 pointer-events-none"></div>

          <div className="relative group z-10">
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-white/10 backdrop-blur-md rounded-2xl border-4 border-white/40 overflow-hidden shadow-2xl transition-transform duration-500 group-hover:-translate-y-2 group-hover:rotate-3 group-hover:border-white">
              <img
                src={
                  profileData.gender === "male"
                    ? "/bg-images/man.png"
                    : "/bg-images/women.png"
                }
                alt="Profile"
                className="w-full h-full object-cover scale-110 group-hover:scale-125 transition-transform duration-500"
              />
            </div>
            {/* Online Status Dot */}
            <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-emerald-500 border-4 border-indigo-700 rounded-full shadow-lg"></div>
          </div>

          <div className="flex flex-col items-center sm:items-start z-10 mb-1">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight drop-shadow-md">{profileData.name || "Advertiser"}</h2>
            <div className="mt-2 flex items-center gap-2">
              <span className="px-3 py-1 bg-white/20 backdrop-blur-md border border-white/30 shadow-inner rounded-md text-[10px] sm:text-xs font-black uppercase tracking-widest">
                {profileData.role || "Brand Partner"}
              </span>
              <span className="px-3 py-1 bg-emerald-500/20 backdrop-blur-md border border-emerald-400/50 text-emerald-100 rounded-md text-[10px] sm:text-xs font-black uppercase tracking-widest flex items-center gap-1">
                Verified
              </span>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6 md:p-8 bg-[#F8FAFC]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 md:gap-6">
            <ProfileField
              icon={FaUser}
              label="Full Name"
              value={profileData.name}
              name="name"
            />
            <ProfileField
              icon={FaEnvelope}
              label="Email Address"
              value={profileData.email}
              name="email"
            />
            <ProfileField
              icon={FaPhone}
              label="Contact Number"
              value={profileData.mobile}
              name="phone"
            />
            <ProfileField
              icon={FaIdCard}
              label="Identification ID"
              value={profileData._id}
              name="advertiserId"
            />
            <ProfileField
              icon={FaMapMarkerAlt}
              label="Residential Address"
              value={profileData.residenceaddress}
              name="address"
            />
            <ProfileField
              icon={FaCity}
              label="Current City"
              value={profileData.city}
              name="city"
            />
            <ProfileField
              icon={FaMapMarkerAlt}
              label="State / Province"
              value={profileData.state}
              name="state"
            />
            <div className="group flex flex-col gap-1.5">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest pl-1">
                Member Since
              </label>
              <div className="flex items-center gap-3 bg-slate-100 p-3 sm:p-4 rounded-xl border-2 border-slate-200 shadow-inner transition-colors">
                <div className="w-8 h-8 rounded-lg bg-slate-200 flex items-center justify-center flex-shrink-0">
                  <FaUserShield className="text-slate-500 w-4 h-4" />
                </div>
                <p className="text-sm sm:text-base font-bold text-slate-600">
                  {profileData.createdAt ? new Date(profileData.createdAt).toLocaleDateString("en-US", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  }).toUpperCase() : "N/A"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={2000} hideProgressBar />
    </div>
  );
};

export default AdvertiserProfile;
